<script lang="ts" setup>
import type { TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import { useI18n } from "vue-i18n";
import { useValidation } from "@/composables/useValidation";
import {
  MandatoryPaymentTypes,
  TravelOrderPaymentStatus as TravelOrderPaymentStatusEnum,
  TravelOrderPaymentType,
  TravelOrderPaymentTypeLabel,
} from "@/constants/travelOrder";
import { computed } from "vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TravelOrderPaymentStatus from "@/components/statuses/TravelOrderPaymentStatus.vue";
import { useOrder } from "@/composables";
import { TravelOrderStatus } from "@/constants/trip";

const { t } = useI18n();
const { requiredValidator } = useValidation();
const { generateId } = useOrder();

const payments = defineModel<TravelOrderPayment[]>({ default: [] });
const props = defineProps<{
  travelOrderStatus: TravelOrderStatus;
}>();
const emits = defineEmits<{
  (e: "scrollBottom"): void;
}>();

const isDisabledTravelOrderStatus = computed(() => {
  return (
    props.travelOrderStatus === TravelOrderStatus.cancelled || props.travelOrderStatus === TravelOrderStatus.noDryRun
  );
});

const typeOptions = computed(() => {
  return Object.entries(TravelOrderPaymentTypeLabel)
    .map(([value, label]) => {
      return {
        id: value,
        name: label,
      };
    })
    .filter((option) => {
      const optionId = option.id as TravelOrderPaymentType;

      return !MandatoryPaymentTypes.includes(optionId);
    });
});

function showDeleteButton(payment: TravelOrderPayment) {
  if (!payment.type) {
    return true;
  }

  return !MandatoryPaymentTypes.includes(payment.type);
}
function addPayment() {
  const newPayment: TravelOrderPayment = {
    amount: null,
    type: null,
    status: TravelOrderPaymentStatusEnum.New,
    tempId: generateId(),
  };

  payments.value = [...payments.value, newPayment];

  emits("scrollBottom");
}

function deletePayment(tempId: string | undefined) {
  payments.value = payments.value.filter((payment) => payment.tempId !== tempId);
}
</script>

<template>
  <div>
    <span class="text-grey-darken-3 font-weight-500">{{ t("general.payments") }}</span>

    <div
      v-for="(payment, index) of payments"
      :key="payment.tempId"
      class="pa-5 pb-2 bg-grey-lighten-5 border border-grey-lighter-2 rounded-lg mt-4"
    >
      <div>
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center col-gap-4">
            <span class="text-grey-darken-2 font-size-12 font-weight-500"
              >{{ t("general.payment") }} #{{ index + 1 }}</span
            >

            <travel-order-payment-status :status="payment.status" />
          </div>

          <v-btn
            v-if="showDeleteButton(payment)"
            class="text-uppercase font-weight-600 font-size-11"
            color="danger"
            size="small"
            variant="text"
            @click="deletePayment(payment.tempId)"
            >{{ t("payments.delete-payment") }}

            <template #prepend>
              <v-icon icon="mdi-delete-outline" size="16"></v-icon>
            </template>
          </v-btn>
        </div>

        <v-row class="mt-4" data-qa="payment-block">
          <v-col cols="3">
            <omni-select
              v-model="payment.type"
              :items="typeOptions"
              :label="t('general.type')"
              :readonly="payment.type !== null && MandatoryPaymentTypes.includes(payment.type)"
              :rules="[requiredValidator]"
              class="required"
              data-qa="modal-payment-type"
              required
            >
              <template v-if="payment.type" #selection>
                {{ TravelOrderPaymentTypeLabel[payment.type] }}
              </template>
            </omni-select>
          </v-col>

          <v-col cols="9">
            <v-text-field
              v-model="payment.amount"
              :disabled="isDisabledTravelOrderStatus"
              :label="t('general.rate')"
              :rules="[requiredValidator]"
              class="required"
              data-qa="modal-rate-field"
              density="compact"
              variant="outlined"
            >
              <template #append-inner>
                <span class="font-size-16 text-grey-darken-1">$</span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-btn
      :disabled="isDisabledTravelOrderStatus"
      class="mt-4 text-uppercase font-size-11"
      color="primary"
      size="small"
      variant="text"
      @click="addPayment"
    >
      <template #prepend>
        <v-icon>mdi-plus</v-icon>
      </template>
      {{ t("payments.add-payment") }}
    </v-btn>
  </div>
</template>

<style lang="scss" scoped></style>
